import {
  Box,
  FileText,
  Users,
  Truck,
  Search,
  ClipboardList,
  ShoppingCart,
  Briefcase,
  Calendar,
  BookOpen,
  FileSpreadsheet,
  Database,
  Camera,
  Images,
  Printer,
  Settings,
  TrendingUp,
  Inbox,
} from 'lucide-react';

import { Tool, ToolGroup } from '../types';

export const DOCS_URL = 'https://docs.um-inc.com';

// The `t` function from useTranslation, narrowed to the calls made here
type Translate = (key: string, defaultValue?: string) => string;

/**
 * UM Tools navigation, grouped and ordered as shown in the sidebar and on the
 * home page. Admin-only tools, appended to the Tools group, show only when
 * isAdmin.
 */
export function getToolGroups(t: Translate, isAdmin = false): ToolGroup[] {
  const adminTools: Tool[] = isAdmin
    ? [
        {
          id: 'printer-admin',
          title: t('navigation.printerAdmin', 'Printer Admin'),
          icon: Settings,
          path: '/admin/printers',
        },
      ]
    : [];

  return [
    {
      id: 'dashboards',
      title: t('navigation.dashboards', 'Dashboards'),
      tools: [
        {
          id: 'parts',
          title: t('navigation.partsDashboard', 'Part Dashboard'),
          description: t('navigation.partsDashboard.description'),
          icon: Box,
          path: '/parts',
        },
        {
          id: 'quote-dashboard',
          title: t('navigation.quoteDashboard', 'Quote Dashboard'),
          description: t('quoteDashboard.description'),
          icon: FileText,
          path: '/quote-dashboard',
        },
        {
          id: 'sales-agent',
          title: t('navigation.salesAgent', 'Sales inbox agent'),
          description: t('salesAgent.description'),
          icon: Inbox,
          path: '/sales-agent',
        },
        {
          id: 'customer-dashboard',
          title: t('navigation.customerDashboard', 'Customer Dashboard'),
          description: t('customerDashboard.subtitle'),
          icon: Users,
          path: '/customers',
        },
        {
          id: 'quote-to-cash',
          title: t('navigation.quoteToCash', 'Quote-to-Cash'),
          description: t('quoteToCash.description'),
          icon: TrendingUp,
          path: '/quote-to-cash',
        },
        {
          id: 'fulfillment',
          title: t('navigation.fulfillment', 'Order Fulfillment'),
          description: t('navigation.fulfillment.description'),
          icon: Truck,
          path: '/fulfillment',
        },
      ],
    },
    {
      id: 'trackers',
      title: t('navigation.trackers', 'Trackers'),
      tools: [
        {
          id: 'quote-tracker',
          title: t('quoteTracker.title', 'Quote Tracker'),
          description: t('quoteTracker.description'),
          icon: Search,
          path: '/quotes',
        },
        {
          id: 'sales-orders',
          title: t('salesOrder.tracker', 'Sales Order Tracker'),
          description: t('salesOrder.trackerDescription'),
          icon: ClipboardList,
          path: '/sales-orders',
        },
        {
          id: 'purchase-orders',
          title: t('purchaseOrder.tracker', 'Purchase Order Tracker'),
          description: t('purchaseOrder.trackerDescription'),
          icon: ShoppingCart,
          path: '/purchase-orders',
        },
        {
          id: 'job-tracker',
          title: t('jobTracker.title', 'Job Tracker'),
          description: t('jobTracker.description'),
          icon: Briefcase,
          path: '/job-tracker',
        },
      ],
    },
    {
      id: 'tools',
      title: t('navigation.tools', 'Tools'),
      tools: [
        {
          id: 'production-schedule',
          title: t('navigation.productionSchedule', 'Production Schedule'),
          description: t('productionSchedule.description'),
          icon: Calendar,
          path: '/production-schedule',
        },
        {
          id: 'docs',
          title: t('navigation.docs', 'Docs'),
          description: t('navigation.docs.description'),
          icon: BookOpen,
          path: DOCS_URL,
          external: true,
        },
        {
          id: 'list-parser',
          title: t('navigation.listParser', 'List Parser'),
          description: t('listParser.description'),
          icon: FileSpreadsheet,
          path: '/list-parser',
        },
        {
          id: 'list-parser-data',
          title: t('navigation.listParserData', 'List Parser Data'),
          description: t('listParserData.description'),
          icon: Database,
          path: '/list-parser-data',
        },
        {
          id: 'picture-processor',
          title: t('navigation.pictureProcessor', 'Picture Processor'),
          description: t('pictureProcessor.description'),
          icon: Camera,
          path: '/picture-processor',
        },
        {
          id: 'picture-manager',
          title: t('navigation.pictureManager', 'Picture Manager'),
          description: t('pictureManager.description'),
          icon: Images,
          path: '/picture-manager',
        },
        {
          id: 'label-printing',
          title: t('navigation.labelPrinting', 'Label Printing'),
          description: t('labelPrinting.description'),
          icon: Printer,
          path: '/label-printing',
        },
        ...adminTools,
      ],
    },
  ];
}

/**
 * Open a tool: in-app routes go through the router, external links open in a new tab
 */
export function openTool(tool: Tool, navigate: (path: string) => void): void {
  if (tool.external) {
    window.open(tool.path, '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(tool.path);
}
